/* ============================================================
   src/shared/draft.ts
   ============================================================
   Cloud draft synchronization. Stores the same JSON shape that
   App.tsx's buildSnapshot() produces — so a draft started on
   phone opens correctly on desktop and vice versa.

   Lives ALONGSIDE the existing .DUest file flow on desktop.
   File save/load behavior is unchanged.

   This module is intentionally framework-agnostic: it takes the
   supabase client as a parameter rather than importing it, so it
   can be exercised from tests or any future caller.
   ============================================================ */

import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  PricingItemRow,
  PricingCategoryRow,
  AddItemRow,
  EstimateInput,
} from "./pricing";
import type { ProposalVisual } from "../visualLibrary";
import { dlog } from "../debugLog";

/** Mirror of App.tsx's buildSnapshot() output (App.tsx:1631). */
export type DraftSnapshot = {
  estimateId: string;
  savedAt: string;
  estimateName: string;

  // customer
  // clientTitle was removed from the app (greeting now uses clientFirstName).
  // Kept here as OPTIONAL so (a) old .DUest / cloud rows that still carry it
  // deserialize without error and (b) mobile, which still reads
  // snapshot.clientTitle, keeps compiling. Desktop no longer writes it.
  clientTitle?: string;
  clientLastName: string;
  clientTown: string;
  clientEmail: string;
  // Client field expansion — additive keys appended after the original four
  // so existing snapshot keys are neither renamed nor reordered. Older cloud
  // rows / .DUest files simply lack these; consumers guard with `|| ""`.
  // Note: clientLocation (street) was never persisted before this change.
  clientFirstName: string;
  clientLocation: string;
  clientState: string;
  clientZip: string;
  clientPhone: string;

  // top-level flags
  constructionType: string;
  includePermit: boolean;
  msrpMode: boolean;

  // selections + quantities
  selectedDeckingId: string;
  deckingSqFt: number;

  selectedRailingId: string;
  railingLf: number;

  selectedStairsId: string;
  stairsCount: number;

  selectedFastenerId: string;

  selectedDemoId: string;
  demoQty: number;

  skirtingCategory: string;
  selectedSkirtingId: string;
  skirtingSf: number;

  miValue: number;

  // user-added line items
  addItems: AddItemRow[];

  // Per-estimate Scope of Work. Empty/missing → ProposalPage falls back to
  // the auto-generated template from current selections.
  sowText?: string;

  // Per-estimate Project Description (one-liner above the Scope of Work).
  // Empty/missing → ProposalPage falls back to the auto-generated summary
  // from constructionType + deckingSqFt. First user edit wins from then on.
  projectDescription?: string;

  // "User has edited this field" flags. Once true, ProposalPage / mobile bind
  // to the raw stored value with no template fallback, so a cleared or custom
  // SOW / Project Description sticks. Missing → false (legacy files unaffected).
  sowTouched?: boolean;
  projectDescriptionTouched?: boolean;

  // Embedded pricing — when a snapshot is opened, these are the
  // frozen prices used by computeEstimate. May be omitted on very
  // old rows; consumers should fall back to live Supabase pricing
  // in that case.
  pricingItems?: PricingItemRow[];
  pricingCategories?: PricingCategoryRow[];
  pricingSnapshotAt?: string;

  // Opaque contract blob — travels with the draft, no mobile
  // editing. Round 3+ wires phone-side contract editing.
  contract?: unknown | null;

  // Matched product photos (URLs only) frozen at save time so the proposal
  // appendix renders for unauthenticated /review customers. Desktop populates
  // this in App.buildSnapshot(); the mobile estimator does not yet embed it.
  visuals?: ProposalVisual[];
};

/** A safe empty draft for fresh users with no cloud row yet. */
export function emptyDraft(): DraftSnapshot {
  return {
    estimateId: "",
    savedAt: new Date().toISOString(),
    estimateName: "",

    clientLastName: "",
    clientTown: "",
    clientEmail: "",
    clientFirstName: "",
    clientLocation: "",
    clientState: "",
    clientZip: "",
    clientPhone: "",

    constructionType: "",
    includePermit: false,
    msrpMode: false,

    selectedDeckingId: "",
    deckingSqFt: 0,

    selectedRailingId: "",
    railingLf: 0,

    selectedStairsId: "",
    stairsCount: 0,

    selectedFastenerId: "",

    selectedDemoId: "",
    demoQty: 0,

    skirtingCategory: "",
    selectedSkirtingId: "",
    skirtingSf: 0,

    miValue: 0,

    addItems: [],
    sowText: "",
    projectDescription: "",
    sowTouched: false,
    projectDescriptionTouched: false,
    contract: null,
  };
}

/** How long the desktop startup draft load may take before it is treated as
 *  failed. The query has no timeout of its own and can hang for 30–60s when
 *  the auth token refresh stalls; while it hangs the estimator is locked. */
export const DRAFT_LOAD_TIMEOUT_MS = 8000;

export type CloudDraftLoadResult =
  | { status: "hit"; snapshot: DraftSnapshot }
  | { status: "miss" }
  | { status: "failed"; reason: string };

/** Load the user's cloud draft, keeping "no draft saved" (miss) apart from
 *  "couldn't find out" (failed: error, exception or timeout). The difference
 *  matters: after a miss it is safe to autosave, after a failure an autosave
 *  would overwrite a real server draft with a blank estimate. Never throws. */
export async function loadCloudDraftWithStatus(
  supabase: SupabaseClient,
  userId: string,
  timeoutMs: number = DRAFT_LOAD_TIMEOUT_MS
): Promise<CloudDraftLoadResult> {
  if (!userId) return { status: "failed", reason: "no userId" };
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const query = supabase
      .from("estimate_drafts")
      .select("snapshot")
      .eq("user_id", userId)
      .maybeSingle();
    const { data, error } = await Promise.race([
      Promise.resolve(query),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () =>
            reject(new Error(`loadCloudDraft timed out after ${timeoutMs}ms`)),
          timeoutMs
        );
      }),
    ]);
    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[draft] loadCloudDraft error:", error.message, error);
      return { status: "failed", reason: error.message };
    }
    if (!data) return { status: "miss" };
    if (!data.snapshot) {
      // A row with no snapshot has nothing worth protecting.
      // eslint-disable-next-line no-console
      console.warn("[draft] loadCloudDraft: row exists but snapshot is null/empty", data);
      return { status: "miss" };
    }
    return { status: "hit", snapshot: data.snapshot as DraftSnapshot };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.warn("[draft] loadCloudDraft failed:", e?.message || e);
    return { status: "failed", reason: String(e?.message || e) };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Load the user's current cloud draft. Returns null if none exists,
 *  or if anything goes wrong (so callers can gracefully fall back to
 *  whatever local state they had). Never throws. Used by the mobile shell;
 *  desktop uses loadCloudDraftWithStatus. */
export async function loadCloudDraft(
  supabase: SupabaseClient,
  userId: string
): Promise<DraftSnapshot | null> {
  if (!userId) {
    dlog("[draft] loadCloudDraft: no userId, skipping");
    return null;
  }
  try {
    const { data, error } = await supabase
      .from("estimate_drafts")
      .select("snapshot")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      // RLS denial or table missing — log and degrade silently.
      // eslint-disable-next-line no-console
      console.warn("[draft] loadCloudDraft error:", error.message, error);
      return null;
    }
    if (!data) {
      dlog("[draft] loadCloudDraft: no row for user", userId);
      return null;
    }
    if (!data.snapshot) {
      // eslint-disable-next-line no-console
      console.warn("[draft] loadCloudDraft: row exists but snapshot is null/empty", data);
      return null;
    }
    dlog("[draft] loadCloudDraft: returning snapshot", {
      estimateId: (data.snapshot as any)?.estimateId,
      clientLastName: (data.snapshot as any)?.clientLastName,
      keys: Object.keys(data.snapshot || {}),
    });
    return data.snapshot as DraftSnapshot;
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.warn("[draft] loadCloudDraft exception:", e?.message || e);
    return null;
  }
}

/** Upsert the user's cloud draft. Fire-and-forget — the local UI never
 *  blocks on the network. Errors are logged but not thrown. */
export async function saveCloudDraft(
  supabase: SupabaseClient,
  userId: string,
  snapshot: DraftSnapshot
): Promise<void> {
  if (!userId) {
    dlog("[draft] saveCloudDraft: no userId, skipping");
    return;
  }
  try {
    dlog("[draft] saveCloudDraft: upserting", {
      userId,
      estimateId: snapshot.estimateId,
      clientLastName: snapshot.clientLastName,
      selectedDeckingId: snapshot.selectedDeckingId,
      deckingSqFt: snapshot.deckingSqFt,
    });
    const { error, status } = await supabase
      .from("estimate_drafts")
      .upsert(
        { user_id: userId, snapshot, updated_at: new Date().toISOString() },
        { onConflict: "user_id" }
      );
    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[draft] saveCloudDraft error:", error.message, error);
    } else {
      dlog("[draft] saveCloudDraft: OK (status " + status + ")");
    }
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.warn("[draft] saveCloudDraft exception:", e?.message || e);
  }
}

/** Adapter: shape a DraftSnapshot into an EstimateInput for
 *  computeEstimate. Pricing rows are passed in separately (mobile
 *  may load them live or use the snapshot's embedded copy). */
export function snapshotToEstimateInput(
  snapshot: DraftSnapshot,
  pricingItems: PricingItemRow[]
): EstimateInput {
  return {
    pricingItems,
    constructionType: snapshot.constructionType || "",
    selectedDeckingId: snapshot.selectedDeckingId || "",
    selectedRailingId: snapshot.selectedRailingId || "",
    selectedStairsId: snapshot.selectedStairsId || "",
    selectedFastenerId: snapshot.selectedFastenerId || "",
    selectedDemoId: snapshot.selectedDemoId || "",
    selectedSkirtingId: snapshot.selectedSkirtingId || "",
    skirtingCategory: snapshot.skirtingCategory || "",
    deckingSqFt: Number(snapshot.deckingSqFt) || 0,
    railingLf: Number(snapshot.railingLf) || 0,
    stairsCount: Number(snapshot.stairsCount) || 0,
    demoQty: Number(snapshot.demoQty) || 0,
    skirtingSf: Number(snapshot.skirtingSf) || 0,
    addItems: Array.isArray(snapshot.addItems) ? snapshot.addItems : [],
    miValue: Number(snapshot.miValue) || 0,
    msrpMode: !!snapshot.msrpMode,
    includePermit: !!snapshot.includePermit,
  };
}

/** One-shot fetch of the live Supabase pricing tables — used by mobile
 *  bootstrap when the cloud draft has no embedded pricing. Mirrors
 *  desktop's bootstrap query at App.tsx ~line 2525. */
export async function fetchLivePricing(
  supabase: SupabaseClient
): Promise<{
  items: PricingItemRow[];
  categories: PricingCategoryRow[];
} | null> {
  try {
    const [itemsRes, catsRes] = await Promise.all([
      supabase
        .from("pricing_items2")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true }),
      supabase
        .from("pricing_categories")
        .select("id, name, is_active")
        .order("name", { ascending: true }),
    ]);
    if (itemsRes.error) {
      // eslint-disable-next-line no-console
      console.warn("[draft] fetchLivePricing items error:", itemsRes.error.message);
      return null;
    }
    if (catsRes.error) {
      // eslint-disable-next-line no-console
      console.warn("[draft] fetchLivePricing categories error:", catsRes.error.message);
      return null;
    }
    return {
      items: (itemsRes.data || []) as PricingItemRow[],
      categories: (catsRes.data || []) as PricingCategoryRow[],
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.warn("[draft] fetchLivePricing exception:", e?.message || e);
    return null;
  }
}

/** Minimal debounce. Returns a function that, when invoked, schedules
 *  the underlying fn to run `ms` milliseconds later. Repeated calls
 *  before the timer fires reset the timer. Includes a `.flush()` to
 *  run pending work immediately (used by mobile unmount cleanup) and
 *  `.cancel()` to discard pending work. */
export function makeDebouncer<TArgs extends any[]>(
  fn: (...args: TArgs) => void,
  ms: number
): {
  (...args: TArgs): void;
  flush: () => void;
  cancel: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: TArgs | null = null;

  const debounced = ((...args: TArgs) => {
    lastArgs = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      if (lastArgs) {
        const a = lastArgs;
        lastArgs = null;
        fn(...a);
      }
    }, ms);
  }) as {
    (...args: TArgs): void;
    flush: () => void;
    cancel: () => void;
  };

  debounced.flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (lastArgs) {
      const a = lastArgs;
      lastArgs = null;
      fn(...a);
    }
  };

  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    lastArgs = null;
  };

  return debounced;
}
